import React, { Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { AGGridProvider } from '@lwt-helix/ag-grid';
import Page from '@lwt-helix/page';
import { Button } from '@lwt-helix/buttons';
import Modal from '@lwt-helix/modal';
import { ToastContainer } from '@lwt-helix/toast';
import UniversalNavBar from './common/components/header/UniversalNavBar';
import ApplicationLayout from './common/components/layout/ApplicationLayout';
import Profile from './common/components/header/profile/Profile';
import SearchBar from './common/components/search/SearchBar';
import AgentProduction from './agent-production/components/AgentProduction';
import AgentProfile from './agent-production/components/agentProfile/AgentProfile';
import SavedAgents from './agent-production/components/SavedAgents';
import {
    buttonTerms,
    drillDownRoutesByModule,
    emails,
    moduleNames,
    moduleRoutes,
    modules,
    modulesByRouteAndNameAssociation,
    searchRoutes as rt,
    searchStatuses
} from './constants';
import ListingInformationPage
    from './agent-production/components/agentProfile/tabs/inventory/listingInformationPage/ListingInformationPage';
import AgentCoverageListing
    from './agent-production/components/agentProfile/tabs/transaction-coverage/coverage-listing/AgentCoverageListing';
import { setStateData } from './common/helpers/state';
import { AgentProductionListings } from './agent-production/components/agentProfile/tabs/production/listingProductionPage/AgentProductionListings';
import MarketShare from './market-share/components/MarketShare';
import { nonModuleRoutes, Routes } from './common/routes/routes';
import { getAllowedModuleByUrl, getFirstToolRoute } from './common/helpers/routes';
import ListingsBreakdown from './market-share/components/ListingsBreakdown';
import OfficesBreakdown from './market-share/components/OfficesBreakdown';
import MDOfficesBreakdown from './market-dynamics/components/Breakdowns';
import ListingInformation from './market-share/components/ListingInformation';
import { getNavItems, getUserItems, helpItems } from './common/helpers/nav';
import MarketDynamics from './market-dynamics/components/MarketDynamics';
import { useCommonStore, useSavedAgentsStore, useSearchStore } from './store/store';
import { useAuthStore } from './store/auth/store';
import { getUserAccounts, getUserAccountsById } from './service/account';
import Logout from './common/components/header/Logout';
import { removeDuplicates } from './common/helpers/utilities';
import { getProductFlags } from './common/helpers/pendo';
import { getUserProducts } from './common/helpers/userPrivileges';
import { getKeyByValue } from './common/helpers/object';
import { getAgentMovement, getProficiencyMetrics } from './service/proficiency-metrics';
import { getCoverageSearch } from './service/market-share';
import { recordAgentListLoad } from './service/service-gateway';
import { useMls } from './common/hooks/mls';
import { usePlatformService } from './common/hooks/usePlatformService';
import { getOwnerIdByTokenInfo, tokenTypes, LWA_TOKEN_OBJECT_IDENTIFIER } from './constants/auth';
import { LinkLwaModal } from './login/components/LinkLwaModal';
import { navigateToLogout, routesToHideNavBar } from './constants/nav';
import { listingsBreakdownRoute, officeBreakdownRoute, officeListingsRoute } from './market-dynamics/constants/routes';
import { views } from './agent-production/constants/savedAgents';
import { responseKeys } from './constants/service';
import { appNames, isAgentMetrics, runningApp } from './constants/app';
import { useMarketDynamicsStore } from './store/marketDynamics/store';
import { getTransformData } from './market-dynamics/helpers/helpers';
import { SuggestedAgents } from './agent-production/components/SuggestedAgents';
import { useFeatureToggles } from './common/hooks/featureToggles';
import { terms as sat } from './agent-production/constants/suggestedAgents';
import Validation from './agent-metrics/components/Validation';
import { VerificationError } from './login/components/VerificationError';
import { AG_GRID_LICENSE } from './common/components/table/agGrid/agGrid';
import Breakdown from './agent-production/agent-movement/Breakdown';
import AgentMovement from './agent-production/agent-movement/AgentMovement';
import { terms as amt } from './agent-production/constants/agentMovement';
import { useSearchResultsStore } from './store/searchResults/store';
import { useTableSettingsStore } from './store/tableSettings/store';
import { getLwaIdFromTokenInfo } from './common/helpers/auth';
import { usePDFStore } from './store/pdf/store';
import { useToastStore } from './store/toast/store';
import Dashboard from './dashboard/components/Dashboard';
import Settings from './agent-production/components/Settings';

const App = () => {
    const [state, setState] = useState({
        agentList: {},
        userInactive: false,
        mlsId: undefined,
        propTypes: undefined,
        mappedAreas: undefined,
        offices: undefined,
        selectedSearchApplied: undefined,
        selectedSavedMarketArea: undefined,
        selectedMarketAreaApplied: undefined,
        allowedModules: undefined,
        navItems: undefined,
        userOffices: undefined,
        userAccounts: undefined,
        userAccount: undefined,
        privileges: undefined,
        isPendoReady: false
    });

    const [agentData, setAgentData] = useState({});

    let location = useLocation();
    const history = useHistory();

    const {
        membership,
        officeInfo,
        timeIntervals,
        getPrivileges,
        getUserProfile,
        getOfficeInfo,
        getMembership,
        getTimeIntervals,
        pendoDataLayers,
        setMlsProviders,
        setPendoDataLayers,
        getMarketAreaLists,
        getOfficeLists,
        getAgentLists,
        setAccountInfo,
        setUserInfo,
        agentMetricsAgentProfileData,
    } = useCommonStore(state => {
        const agentInfo = state?.agentInfo?.[0];
        const agentMetricsAgentProfileData = {
            agentId: agentInfo?.mlsAgentId?.agentId,
            mlsSid: agentInfo?.mlsAgentId?.mlsId,
            agentName: `${agentInfo?.firstName} ${agentInfo?.lastName}`,
            officeName: agentInfo?.office?.name,
        };
        return ({
            userInfo: state.userInfo,
            membership: state.membership,
            officeInfo: state.officeInfo,
            timeIntervals: state.timeIntervals,
            getPrivileges: state.getPrivileges,
            getUserProfile: state.getUserProfile,
            getOfficeInfo: state.getOfficeInfo,
            getMembership: state.getMembership,
            getTimeIntervals: state.getTimeIntervals,
            pendoDataLayers: state.pendoDataLayers,
            setMlsProviders: state.setMlsProviders,
            setPendoDataLayers: state.setPendoDataLayers,
            getMarketAreaLists: state.getMarketAreaLists,
            getOfficeLists: state.getOfficeLists,
            getAgentLists: state.getAgentLists,
            setAccountInfo: state.setAccountInfo,
            setUserInfo: state.setUserInfo,
            agentMetricsAgentProfileData,
        });
    });

    const selectedModule = useSearchStore(state => state.selectedModule);

    const setModuleAgentData = (data) => {
        setAgentData(prev => ({
            ...prev,
            [selectedModule]: data
        }));
    };

    const {
        tokenInfo,
        tokenType,
        handleShouldShowLinkLwaModal,
        isLegacyUser,
        initAgentMetricsApp,
        isXamUserValidated,
    } = useAuthStore(state => ({
        tokenInfo: state.tokenInfo,
        tokenType: state.tokenType,
        handleShouldShowLinkLwaModal: state.handleShouldShowLinkLwaModal,
        isLegacyUser: state.isLegacyUser,
        initAgentMetricsApp: state.initAgentMetricsApp,
        isXamUserValidated: state.isXamUserValidated,
    }));

    const isLWAAuthenticated = tokenType === tokenTypes.LWA;
    const { data: platformService } = usePlatformService(isLWAAuthenticated);
    const [lwaClients, setLwaClients] = useState(undefined);

    useEffect(() => {
        if (!platformService) return;
        platformService.getLwaClients().then(setLwaClients);
    }, [platformService]);

    const {
        agentDataStore,
        setTotalsRowData,
        setSummaryPinnedData,
        selectedMls,
        setSelectedModule,
        getSavedSearchList,
        setSelectedMls,
        searchStatus,
        setLoadedSearch,
        setIsLoading,
        setIsLoadingSearchData,
        setSearchStatus,
        search,
        loadedSearchCriteria,
        selectedSavedSearch,
        runMarketShareTotalsSearch,
        isManualSearch,
        clearSavedAgentsOptionalFilterTitles,
        clearSelectedAgentIds,
    } = useSearchStore(state => ({
        agentDataStore: state[selectedModule]?.agentData,
        setTotalsRowData: state.setTotalsRowData,
        setSummaryPinnedData: state.setSummaryPinnedData,
        selectedMls: state[selectedModule]?.selectedMls,
        setSelectedModule: state.setSelectedModule,
        getSavedSearchList: state.getSavedSearchList,
        setSelectedMls: state.setSelectedMls,
        searchStatus: state[selectedModule]?.searchStatus,
        setLoadedSearch: state.setLoadedSearch,
        setIsLoading: state.setIsLoading,
        setIsLoadingSearchData: state.setIsLoadingSearchData,
        setSearchStatus: state.setSearchStatus,
        search: state[selectedModule]?.search,
        loadedSearchCriteria: state[selectedModule]?.loadedSearchCriteria,
        selectedSavedSearch: state[selectedModule]?.selectedSavedSearch,
        runMarketShareTotalsSearch: state.runMarketShareTotalsSearch,
        isManualSearch: state.isManualSearch,
        clearSavedAgentsOptionalFilterTitles: state.clearSavedAgentsOptionalFilterTitles,
        clearSelectedAgentIds: state.clearSelectedAgentIds,
    }));

    const {
        searchResultsStore,
        setSearchResults,
    } = useSearchResultsStore(state => ({
        searchResultsStore: state[selectedModule]?.searchResults?.results,
        setSearchResults: state.setSearchResults,
    }));

    const agentNameModule = modules.proficiencyMetrics.agents;
    const savedAgentsModule = modules.proficiencyMetrics.savedAgents;

    const {
        findAgentListById,
        setGlobalSavedAgentListId,
        setSelectedListByModule,
    } = useSavedAgentsStore(state => ({
        findAgentListById: state.findAgentListById,
        setGlobalSavedAgentListId: state.setGlobalSavedAgentListId,
        setSelectedListByModule: state.setSelectedListByModule,
    }));

    const runMarketDynamicsSearch = useMarketDynamicsStore(state => state.runMarketDynamicsSearch);

    const { resetTableSettings } = useTableSettingsStore(state => ({
        resetTableSettings: state.resetTableSettings,
    }));

    const fetchLogo = usePDFStore(state => state.fetchLogo);

    const { toastProps } = useToastStore(state => ({
        toastProps: state.toastProps,
    }));

    useEffect(() => {
        const checkPendoReady = () => {
            if (window.pendo) {
                setStateData('isPendoReady', true, setState);
            } else {
                setTimeout(checkPendoReady, 50);
            }
        };

        checkPendoReady();
        clearSavedAgentsOptionalFilterTitles();
    }, []);

    const ownerId = tokenType && getOwnerIdByTokenInfo[tokenType](tokenInfo);
    // We have different Agent Data location, depending of the module.
    const agentDataState = agentData?.[selectedModule];
    const selectedAgentData = agentDataState || agentDataStore || searchResultsStore || agentDataState?.agentMovementSearch;
    const featureToggles = useFeatureToggles();

    const initializePendo = (pendoDataLayers) => {
        window.pendo?.initialize(pendoDataLayers);
    };

    const localToken = JSON.parse(localStorage.getItem('auth_state'))?.state?.token;

    // This checks if the session was ended in another tab
    useEffect(() => {
        if (!localToken) navigateToLogout();
    },[localToken]);
    const isAgentProductionModule = selectedModule === modules.agentProduction.agentProduction;
    const mlsId = (isAgentMetrics && isAgentProductionModule)
        ? agentMetricsAgentProfileData.mlsSid
        : selectedMls?.mlsId;
    const mlsProps = useMls(mlsId, selectedModule);

    useEffect(() => {
        if (!pendoDataLayers && !isLegacyUser && !state.isPendoReady) return;
        initializePendo(pendoDataLayers);
        window.pendo?.updateOptions(pendoDataLayers);
    }, [pendoDataLayers, state.isPendoReady]);

    const isAgentProductionEnabled = featureToggles.agentProduction.isEnabled;

    useEffect(async () => {
        if (!tokenInfo || !state.isPendoReady) return;
        await fetchLogo();

        if ( mlsProps
            && officeInfo
            && timeIntervals
            && loadedSearchCriteria
            && (searchStatus !== searchStatuses.running || searchStatus !== searchStatuses.ran)
        ) {
            setSearchStatus(searchStatuses.run);
            setIsLoadingSearchData(true);
            setIsLoading(true);
        }

        if (isAgentMetrics) {
            initAgentMetricsApp({
                setState,
                setPendoDataLayers,
                tokenInfo,
                getUserLists,
                history,
                isLegacyUser,
                runningApp,
                ownerId,
                isAgentProductionEnabled,
            });
            return;
        }

        let userAccounts;
        if (tokenType === tokenTypes.LWA) {
            const xbmUserIdFromToken = tokenInfo[LWA_TOKEN_OBJECT_IDENTIFIER]?.apps?.xbm?.uid;
            const lwaId = getLwaIdFromTokenInfo(tokenInfo);
            userAccounts = await getUserAccountsById(xbmUserIdFromToken, lwaId);
            if (userAccounts.error === 404) navigateToLogout();
            setStateData('userAccounts', userAccounts, setState);
        }
        if (tokenType === tokenTypes.KEYCLOAK) {
            userAccounts = await getUserAccounts(tokenInfo.email);
        }

        if (userAccounts.error && userAccounts.error !== 404) return;

        // if there are no userAccounts returned, then this indicates old legacy setup
        if (!userAccounts.accounts?.length) {
            const userProducts = getUserProducts({ isLegacyUser: true });
            setStateData('allowedModules', userProducts, setState);
            const profile = await getUserProfile(tokenInfo.preferred_username);
            if (profile.error) return;

            if (!profile.isSetupActive || !profile.isUserActive) {
                setStateData('userInactive', true, setState);
                return;
            }
            const productFlags = getProductFlags(userProducts);
            setPendoDataLayers({ tokenInfo, productFlags, isLegacyUser, runningApp, ownerId });
            if (!!profile?.accessibleOfficeList?.length) await getOfficeInfo(profile?.accessibleOfficeList);
            await getMembership(profile.mlsList);
            await getTimeIntervals();
            await getUserLists(ownerId);
            const userOffices = profile.accessibleOfficeList.reduce((acc, office) => {
                const existingMls = acc.find(item => item.mlsId === office.mlsId);
                if (existingMls) {
                    existingMls.officeIds.push(office.officeId);
                } else {
                    acc.push({mlsId: office.mlsId, officeIds: [office.officeId]});
                }
                return acc;
            }, []);
            useCommonStore.setState({ privileges: { userOffices } });
            return;
        }

        //TODO: add account selector logic here, right now select the first option
        const selectedAccount = userAccounts?.accounts[0];

        const isAccountUserInactive = !selectedAccount.accountActive || !selectedAccount.userActive

        // This is part of the A/C of XBM-7408 and could be modified after the demo
        if (isAccountUserInactive && !isAgentMetrics) {
            setStateData('userInactive', true, setState);
            return;
        }

        const hasLwaId = !!userAccounts?.user?.lwaId;
        setStateData('userAccount', selectedAccount, setState);
        setAccountInfo(selectedAccount);
        setUserInfo(userAccounts?.user);

        handleShouldShowLinkLwaModal(hasLwaId);

        const privileges = await getPrivileges(selectedAccount.id, userAccounts.user.id);
        const userProducts = getUserProducts({ privileges });
        const productFlags = getProductFlags(userProducts);
        setPendoDataLayers({ tokenInfo, selectedAccount, productFlags, isLegacyUser, runningApp, ownerId });

        if (privileges?.error) return;

        setStateData('allowedModules', privileges.userProducts, setState);

        if (searchStatus === searchStatuses.running) setSearchStatus(searchStatuses.run);

        const activeValidOffices = await setMlsListForMembershipLookup(privileges);
        activeValidOffices && await getMembership(activeValidOffices?.map(o => o.mlsOfficeId.mlsId));
        await getTimeIntervals();
        await getUserLists(ownerId);
    }, [tokenInfo, state.isPendoReady, isXamUserValidated]);

    useEffect(() => {
        membership && setStateData('mlsList', membership, setState);
    }, [membership]);

    useEffect(() => {
        officeInfo && setStateData('userOffices', officeInfo, setState);
    }, [officeInfo]);

    useEffect(async () => {
        //run search again if not already loaded (refresh)
        //we dont want this for suggestedAgents module, since we are not running a search manually.
        if (selectedModule === modules.proficiencyMetrics.suggestedAgents) return;

        if (loadedSearchCriteria && !agentData?.[selectedModule] && (!searchStatus || searchStatus === searchStatuses.ran)) {
            setLoadedSearch();
            setSearchStatus(searchStatuses.run);
        }
    }, [loadedSearchCriteria, agentData]);

    useEffect(async () => {
        if (!tokenInfo) return;
        if (searchStatus === searchStatuses.run) {
            setIsLoading(true);
            setIsLoadingSearchData(true);
            setLoadedSearch();
            if (isManualSearch) clearSelectedAgentIds();
            setModuleAgentData(undefined);
            setStateData('agentList', undefined, setState)
            try {
                resetTableSettings();
                await runSearch();
            } catch {
                setIsLoadingSearchData(false);
            }
        } else if (searchStatus === searchStatuses.ran) {
            setIsLoading(false);
            setIsLoadingSearchData(false);
            setSearchStatus(undefined);
            if (isManualSearch) useSearchStore.setState({ isManualSearch: false });
        }
    }, [searchStatus]);

    useEffect(async () => {
        if (!!state.mlsList?.length) {
            const activeMlsList = state.mlsList.filter(mls => mls.isActive);
            const activeMlsListWithActiveOffices = !state.userOffices?.length
                ? activeMlsList
                : activeMlsList
                    .filter(mls => !!state.userOffices
                        .find(office => office.isOfficeActive && office.mlsOfficeId.mlsId === mls.mlsId)
                    );
            if (activeMlsListWithActiveOffices?.length === 1) {
                if (selectedModule === modules.proficiencyMetrics.suggestedAgents ||
                    selectedModule === modules.dashboard.dashboard) {
                    setSelectedMls(activeMlsListWithActiveOffices[0], selectedModule, timeIntervals,
                        activeMlsListWithActiveOffices?.length);
                } else {
                    if (selectedSavedSearch) return;
                    setMlsProviders(activeMlsListWithActiveOffices);
                    !loadedSearchCriteria && await setSelectedMls(
                        activeMlsListWithActiveOffices[0],
                        selectedModule,
                        timeIntervals,
                        activeMlsListWithActiveOffices?.length
                    );
                }
            } else {
                const sortFunction = (a, b) => a.shortDescription.localeCompare(b.shortDescription);
                activeMlsListWithActiveOffices && setMlsProviders(activeMlsListWithActiveOffices.sort(sortFunction));
                if ((selectedModule === modules.proficiencyMetrics.suggestedAgents ||
                    selectedModule === modules.dashboard.dashboard) && !selectedMls?.mlsId) {
                    setSelectedMls(activeMlsListWithActiveOffices[0], selectedModule, timeIntervals,
                        activeMlsListWithActiveOffices?.length);
                }
            }
        }
    }, [state.mlsList, state.userOffices, selectedModule, selectedMls]);

    const handleDashboardRedirect = async () => {
        const isDashboardEnabled = featureToggles.dashboard.isEnabled;
        const isOnRootPath = location.pathname === '/';
        const isOnDashboardPath = location.pathname === Routes.HOME.DASHBOARD;

        if (isDashboardEnabled && (isOnRootPath || isOnDashboardPath)) {
            await setSelectedModule(modules.dashboard.dashboard);
            await navTo(moduleRoutes[modules.dashboard.dashboard], modules.dashboard.dashboard);
            return true;
        }
        return false;
    };

    useEffect(async () => {
        if (state.allowedModules && !selectedModule) {

            const isNonModuleRoute = nonModuleRoutes.some(route => location.pathname.startsWith(route));
            if (isNonModuleRoute) return;

            const redirectedToDashboard = await handleDashboardRedirect();
            if (redirectedToDashboard) return;

            const initialModuleToLoad = getAllowedModuleByUrl({
                url: location.pathname,
                allowedModules: state.allowedModules,
                modulesByRouteAndNameAssociation: modulesByRouteAndNameAssociation
            });
            await setSelectedModule(initialModuleToLoad);
            await navTo(moduleRoutes[initialModuleToLoad], initialModuleToLoad);
        }
    }, [state.allowedModules, selectedModule]);

    useEffect(() => {
        if (!state.allowedModules) return;
        const isDashboardEnabled = featureToggles.dashboard.isEnabled;
        const isTalentEnabled = featureToggles.talent.isEnabled;
        let filteredNavItems = getNavItems({
            allowedModules: state.allowedModules, navTo, isAgentProductionEnabled, isDashboardEnabled, isLegacyUser, isTalentEnabled, lwaClients
        });
        if (!featureToggles.suggestedAgents.isEnabled) {
            filteredNavItems = filteredNavItems.map(item => ({
                ...item,
                menuItems: item?.menuItems?.filter(item => item.label !== sat.suggestedAgents)
            }));
        }
        if (!featureToggles.agentMovement.isEnabled) {
            filteredNavItems = filteredNavItems.map(item => ({
                ...item,
                menuItems: item?.menuItems?.filter(item => item.label !== amt.agentMovement)
            }));
        }
        setStateData('navItems', filteredNavItems, setState);
    }, [state.allowedModules, isLegacyUser, lwaClients]);

    useEffect(async () => {
        if (location && location.pathname !== '/' && selectedModule && location.pathname !== moduleRoutes[selectedModule]) {
            const nextModule = getKeyByValue(moduleRoutes, location.pathname)
                || Object.keys(moduleRoutes).find(key => location.pathname.startsWith(moduleRoutes[key]));
            if (nextModule) {
                await setSelectedModule(nextModule);
            }
        }
    }, [location]);

    const validateIsManualSearch = () => {
        if (isManualSearch) {
            useSearchStore.setState({ isManualSearch: false });
            setIsLoading(false);
            setIsLoadingSearchData(false);
        }
    };

    const runSearch = async () => {
        const searchCriteria = search?.searchCriteria;
        if (searchCriteria) {
            setSearchStatus(searchStatuses.running);
            switch (selectedModule) {
                case modules.proficiencyMetrics.transactions:
                case modules.proficiencyMetrics.offices:
                case modules.proficiencyMetrics.savedAgents:
                case agentNameModule:
                    try {
                        const { recruitingSoldResponse, recruitingSoldTotals, error } = await getProficiencyMetrics(searchCriteria);
                        if (!error) {
                            setModuleAgentData(recruitingSoldResponse);
                            setTotalsRowData(recruitingSoldTotals, responseKeys.recruitingSoldTotals);
                            setSummaryPinnedData(views.overview, responseKeys.recruitingSoldTotals);
                            if (selectedModule === savedAgentsModule) break;
                            if (selectedModule !== agentNameModule) break;
                            const selectedAgentList = findAgentListById();
                            if (selectedAgentList) {
                                setSelectedListByModule(selectedModule, selectedAgentList);
                                setSelectedListByModule(modules.proficiencyMetrics.savedAgents, {});
                                setGlobalSavedAgentListId(selectedAgentList?.agentListId);
                                recordAgentListLoad(selectedAgentList.agentListId).catch(() => {});
                            }
                        }
                    } catch (err) {
                        console.log(err);
                    }
                    break;
                case modules.proficiencyMetrics.agentMovement:
                    try {
                        const { results, totals } = await getAgentMovement(searchCriteria);
                        setModuleAgentData({ agentMovementSearch: results });
                        setTotalsRowData(totals, responseKeys.totals);
                        setSearchResults(results.results);
                    } catch (error) {
                        console.error({error})
                    }
                    break;
                case modules.marketShare.totals:
                    await runMarketShareTotalsSearch(searchCriteria, setAgentData);
                    validateIsManualSearch();
                    break;
                case modules.marketShare.coverage:
                    const coverageSearchResponse = await getCoverageSearch(searchCriteria);
                    if (!coverageSearchResponse.error) {
                        setModuleAgentData({ coverageSearch: coverageSearchResponse });
                    }
                    validateIsManualSearch();
                    break;
                case modules.marketDynamics.marketDynamics:
                    const { results, totals } = await runMarketDynamicsSearch(searchCriteria);
                    setModuleAgentData({ marketDynamicsSearch: results });
                    useCommonStore.setState({ getSearchResults: () => getTransformData(results) });
                    setTotalsRowData(totals, responseKeys.totals);
                    break;
                default:
                    break;
            }
            setSearchStatus(searchStatuses.ran);
        }
    };

    const getUserLists = async (ownerId) => {
        await getMarketAreaLists(ownerId);
        await getSavedSearchList(ownerId);
        setIsLoadingSearchData(false);
        if (isAgentMetrics) return;
        await getOfficeLists(ownerId);
        await getAgentLists(ownerId);
    };

    const userAllowedTo = (module) => {
        return module && (!state.allowedModules?.length || state.allowedModules.includes(module));
    };

    const navTo = async (destination, module) => {
        history.replace(destination);
        module && await setSelectedModule(module);
    };

    /// validate the user's settings, so we're only using the MLSs for which the user belongs to an active office
    const setMlsListForMembershipLookup = async (privileges) => {
        let activeOffices = [];
        let mlsOffices = [];
        if (!!privileges.userOffices?.length) {
            mlsOffices = [].concat(...privileges.userOffices.map(group => {
                return [].concat(...group.officeIds.map(officeId => ({ officeId: officeId, mlsId: group.mlsId })));
            }));
        }

        if (mlsOffices) {
            const response = await getOfficeInfo(removeDuplicates(mlsOffices));
            if (!response?.error) {
                activeOffices = response.filter(office => !!office.isOfficeActive );
                activeOffices.length === 0 && setStateData('userInactive', true, setState);
            }
        }
        return activeOffices;
    };

    const shouldShowNavBar = !routesToHideNavBar.includes(location.pathname);

    const isAppInitialized = state.navItems && tokenInfo;

    const navItemsForLayout = shouldShowNavBar && state.navItems ? state.navItems : null;

    return (
        <div className='App'>
            <Modal
                title='Your login credential is inactive'
                show={state.userInactive}
                onClose={navigateToLogout}
                buttons={<div className='w-100 text-center'>
                    <Button
                        dataLwtId='logout-button'
                        color='primary'
                        onClick={navigateToLogout}
                    >
                        {buttonTerms.ok}
                    </Button>
                </div>
                }
            >
                <div className='text-center'>
                    <p>Please contact our Customer Support at</p>
                    <p>
                        <a href={`mailto:${emails.terradatumSupport}`}>{emails.terradatumSupport}</a> or call
                        888-212-4793
                    </p>
                </div>
            </Modal>
            <LinkLwaModal />
            <Suspense fallback={<div>Loading...</div>}>
                <Route exact path={Routes.AUTH.LOG_OUT}>
                    <Logout />
                </Route>

                {isAgentMetrics && (
                    <Switch>
                        <Route exact path={Routes.VALIDATION.BASE}>
                            <Page fluid hero>
                                <Validation />
                            </Page>
                        </Route>
                        <Route exact path={Routes.AUTH.VERIFICATION_ERROR}>
                            <Page fluid hero>
                                <VerificationError />
                            </Page>
                        </Route>
                    </Switch>
                )}

                {shouldShowNavBar && state.navItems && (
                    <UniversalNavBar
                        module={selectedModule}
                        userItems={getUserItems(navTo, isLegacyUser)}
                        helpItems={helpItems}
                        navItems={state.navItems}
                    >
                        <ApplicationLayout navItems={navItemsForLayout}>
                            {isAppInitialized && (<AGGridProvider license={AG_GRID_LICENSE}>
                                <Switch>
                                    {featureToggles.dashboard.isEnabled && (<Route exact path={Routes.HOME.DASHBOARD}>
                                        <Page fluid hero showFooter={false}>
                                            <Dashboard
                                                module={selectedModule}
                                                mlsMembershipInfo={membership}
                                                mlsProps={mlsProps}
                                                userId={ownerId}
                                                userInfo={isLegacyUser ? tokenInfo : state.userAccounts?.user}
                                            />
                                        </Page>
                                    </Route>)}
                                    {userAllowedTo(moduleNames.proficiencyMetrics) && ([
                                        <Route key={rt.transactions} exact path={rt.transactions}>
                                            <SearchBar
                                                key='agent-production'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                <AgentProduction agentData={selectedAgentData}
                                                                module={modules.proficiencyMetrics.transactions} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.officeName} exact strict path={rt.officeName}>
                                            <SearchBar
                                                key='office-name'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                <AgentProduction agentData={selectedAgentData}
                                                                module={modules.proficiencyMetrics.offices} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.agentName} exact strict path={rt.agentName}>
                                            <SearchBar
                                                key='agent-name'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                <AgentProduction agentData={selectedAgentData}
                                                                module={agentNameModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.AGENT_PROFILE}
                                            path={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.AGENT_PROFILE}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentProfile
                                                    agentData={selectedAgentData}
                                                    module={selectedModule}
                                                    mlsProps={mlsProps}
                                                    tokenInfo={tokenInfo}
                                                />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.COVERAGE_LISTING}
                                            path={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.COVERAGE_LISTING}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentCoverageListing module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.LISTING_INFO}
                                            path={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.LISTING_INFO}>
                                            <Page fluid hero showFooter={false}>
                                                <ListingInformationPage agentData={selectedAgentData}
                                                    module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.PROD_LISTING}
                                            path={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.PROD_LISTING}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentProductionListings agentData={selectedAgentData}
                                                    module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.agentMovement} exact path={rt.agentMovement}>
                                            <SearchBar
                                                key='agent-movement'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                {featureToggles.agentMovement.isEnabled
                                                    ? <AgentMovement agentData={selectedAgentData?.agentMovementSearch}/>
                                                    : <Redirect to={rt.transactions} />
                                                }
                                            </Page>
                                        </Route>,
                                        <Route key={rt.agentsGainedBreakdown} exact path={[rt.agentsGainedBreakdown, rt.officeListBreakdown]}>
                                            <Page fluid hero showFooter={false}>
                                                <Breakdown setAgentData={setAgentData}/>
                                            </Page>
                                        </Route>,
                                        <Route key={rt.savedAgents} exact path={[rt.savedAgents, rt.savedAgentsActivity]}>
                                            <SearchBar
                                                key='saved-agents'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                <SavedAgents setModuleAgentData={setModuleAgentData} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.suggestedAgents} exact strict path={rt.suggestedAgents}>
                                            <SearchBar
                                                key='suggested-agents-name'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                {featureToggles.suggestedAgents.isEnabled
                                                    ? <SuggestedAgents />
                                                    : <Redirect to={rt.transactions} />
                                                }
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.SETTINGS}
                                            path={Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.SETTINGS}>
                                            <Page fluid hero showFooter={false}>
                                                <Settings isMarketShareAllowed={userAllowedTo(moduleNames.marketShare)} />
                                            </Page>
                                        </Route>,
                                    ])}
                                    {userAllowedTo(moduleNames.marketShare) && ([
                                        <Route key={rt.totals + Routes.MARKET_SHARE.OFFICE_LISTING}
                                            path={[
                                                rt.totals + Routes.MARKET_SHARE.OFFICE_LISTING,
                                                rt.coverage + Routes.MARKET_SHARE.OFFICE_LISTING
                                            ]}>
                                            <Page fluid hero showFooter={false}>
                                                <ListingsBreakdown module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.totals + Routes.MARKET_SHARE.OFFICES_BREAKDOWN}
                                            path={[
                                                rt.totals + Routes.MARKET_SHARE.OFFICES_BREAKDOWN,
                                                rt.coverage + Routes.MARKET_SHARE.OFFICES_BREAKDOWN
                                            ]}>
                                            <Page fluid hero showFooter={false}>
                                                <OfficesBreakdown module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={rt.totals} path={rt.totals}>
                                            <SearchBar
                                                key='market-share-totals'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                {selectedModule === modules.marketShare.totals &&
                                                <AGGridProvider license={AG_GRID_LICENSE}>
                                                    <MarketShare
                                                        agentData={agentData?.[modules.marketShare.totals]}
                                                        module={modules.marketShare.totals} />
                                                </AGGridProvider>
                                                }
                                            </Page>
                                        </Route>,
                                        !isAgentMetrics && (<Route key={rt.coverage} path={rt.coverage}>
                                            <SearchBar
                                                key='market-share-coverage'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                            />
                                            <Page fluid hero showFooter={false}>
                                                {selectedModule === modules.marketShare.coverage &&
                                                <AGGridProvider license={AG_GRID_LICENSE}>
                                                    <MarketShare agentData={agentData?.[modules.marketShare.coverage]}
                                                                module={modules.marketShare.coverage} />
                                                </AGGridProvider>
                                                }
                                            </Page>
                                        </Route>),
                                        <Route key={Routes.MARKET_SHARE.BASE + Routes.MARKET_SHARE.LISTING_INFO}
                                            path={Routes.MARKET_SHARE.BASE + Routes.MARKET_SHARE.LISTING_INFO}>
                                            <Page fluid hero showFooter={false}>
                                                <ListingInformation module={selectedModule} />
                                            </Page>
                                        </Route>
                                    ])}
                                    {userAllowedTo(moduleNames.marketDynamics) && ([
                                        <Route key={[drillDownRoutesByModule[moduleNames.marketDynamics].listingInfo]}
                                            path={[drillDownRoutesByModule[moduleNames.marketDynamics].listingInfo]}>
                                            <Page fluid hero showFooter={false}>
                                                <ListingInformation module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={[officeBreakdownRoute, listingsBreakdownRoute, officeListingsRoute]}
                                            path={[officeBreakdownRoute, listingsBreakdownRoute, officeListingsRoute]}>
                                            <Page fluid hero showFooter={false}>
                                                <MDOfficesBreakdown />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.MARKET_DYNAMICS.BASE} path={Routes.MARKET_DYNAMICS.BASE}>
                                            <SearchBar
                                                key='market-dynamics'
                                                module={selectedModule}
                                                mlsProps={mlsProps}
                                                />
                                            <Page fluid hero showFooter={false}>
                                                {selectedModule === moduleNames.marketDynamics &&
                                                <MarketDynamics agentData={agentData?.[modules.marketDynamics.marketDynamics]}
                                                module={selectedModule} />
                                            }
                                            </Page>
                                        </Route>
                                    ])}
                                    {userAllowedTo(moduleNames.agentProduction) && ([
                                        <Route key={Routes.AGENT_PRODUCTION.BASE} exact path={Routes.AGENT_PRODUCTION.BASE}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentProfile
                                                    agentData={[agentMetricsAgentProfileData]}
                                                    module={selectedModule}
                                                    mlsProps={mlsProps}
                                                    tokenInfo={tokenInfo}
                                                />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.PROD_LISTING}
                                            exact path={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.PROD_LISTING}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentProductionListings
                                                    agentData={selectedAgentData}
                                                    module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.COVERAGE_LISTING}
                                            exact path={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.COVERAGE_LISTING}>
                                            <Page fluid hero showFooter={false}>
                                                <AgentCoverageListing module={selectedModule} />
                                            </Page>
                                        </Route>,
                                        <Route key={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.LISTING_INFO}
                                            exact path={Routes.AGENT_PRODUCTION.BASE + Routes.PROF_METRICS.LISTING_INFO}>
                                            <Page fluid hero showFooter={false}>
                                                <ListingInformationPage
                                                    agentData={selectedAgentData}
                                                    module={selectedModule} />
                                            </Page>
                                        </Route>,
                                    ])}
                                    <Route path={Routes.MY_PROFILE.BASE}>
                                        <Page fluid hero showFooter={false}>
                                            <Profile userAccounts={state.userAccounts}
                                                    isMarketShareAllowed={userAllowedTo(moduleNames.marketShare)}
                                            />
                                        </Page>
                                    </Route>
                                    {state.allowedModules && <>
                                        <Route exact path='/'>
                                            <Redirect to={getFirstToolRoute(state.allowedModules)} />
                                        </Route>
                                        <Route path='*'>
                                            <Redirect to={getFirstToolRoute(state.allowedModules)} />
                                        </Route>
                                    </>
                                    }
                                </Switch>
                            </AGGridProvider>
                            )}
                        </ApplicationLayout>
                    </UniversalNavBar>
                )}
            </Suspense>
            {toastProps && (
                <ToastContainer
                    dataLwtId='global-api-error-toast'
                    toastProps={toastProps}
                    position='toast-bottom-right'
                />
            )}
        </div>
    );
};

export default App;
